import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/osagest-theme.css';
import { 
  Receipt, Users, Headphones, BookOpen, Camera, Building2, 
  Clock, Calendar, CheckCircle2, ArrowRight, Star, Zap, Shield, Workflow 
} from 'lucide-react';

function LandingOSAGEST() {
  const navigate = useNavigate();
  
  const modules = [
    {
      icon: <Receipt className="h-8 w-8" />,
      title: "Facturación Electrónica",
      description: "Facturas, presupuestos, Verifactu y SEPA. Cumple normativa española"
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "CRM & Clientes",
      description: "Gestión completa de clientes para autónomos y pymes"
    },
    {
      icon: <Headphones className="h-8 w-8" />,
      title: "Sistema de Ticketing",
      description: "Gestión de incidencias y soporte a clientes"
    },
    {
      icon: <BookOpen className="h-8 w-8" />,
      title: "Contabilidad Completa",
      description: "Asientos contables, libros oficiales, modelos 303 y 130"
    },
    {
      icon: <Camera className="h-8 w-8" />,
      title: "OCR Inteligente",
      description: "Digitaliza facturas y tickets automáticamente con IA"
    },
    {
      icon: <Building2 className="h-8 w-8" />,
      title: "Gestión de Proveedores",
      description: "Control de compras y facturas recibidas"
    },
    {
      icon: <Clock className="h-8 w-8" />,
      title: "Control Horario",
      description: "Fichaje desde el móvil y registro diario de jornada"
    },
    {
      icon: <Calendar className="h-8 w-8" />,
      title: "Calendario y Eventos",
      description: "Planificación y organización del equipo"
    }
  ];

  const plans = [
    {
      name: "Básico",
      price: "49",
      popular: false,
      features: [
        "Facturación Electrónica",
        "CRM & Clientes (hasta 500)",
        "1 usuario",
        "Soporte por email"
      ]
    },
    {
      name: "Profesional",
      price: "99",
      popular: true,
      features: [
        "Todo lo del Plan Básico",
        "Sistema de Ticketing",
        "Contabilidad Avanzada",
        "OCR Inteligente (100 tickets/mes)",
        "Hasta 3 usuarios",
        "Clientes ilimitados",
        "Soporte prioritario"
      ]
    },
    {
      name: "Empresarial",
      price: "199",
      popular: false,
      features: [
        "Todo lo del Plan Profesional",
        "Gestión de Proveedores",
        "Control Horario",
        "Calendario Teamup",
        "OCR Ilimitado",
        "Usuarios ilimitados",
        "Soporte prioritario 24/7"
      ]
    }
  ];

  const features = [
    {
      icon: <Zap size={28} />,
      title: "Automatización Inteligente",
      description: "Ahorra tiempo con procesos automatizados"
    },
    {
      icon: <Shield size={28} />,
      title: "Máxima Seguridad",
      description: "Datos protegidos y cumplimiento normativo"
    },
    {
      icon: <Workflow size={28} />,
      title: "Integración Total",
      description: "Todos los módulos conectados en tiempo real"
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#FFFFFF',
      fontFamily: 'Inter, sans-serif'
    }}>
      {/* Hero Section con diseño tecnológico */}
      <div style={{
        background: 'linear-gradient(135deg, #0066B3 0%, #004D85 100%)',
        position: 'relative',
        overflow: 'hidden',
        paddingTop: '80px',
        paddingBottom: '100px'
      }}>
        {/* Animated tech background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: `
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(0deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }} />
        
        {/* Floating geometric shapes */}
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '10%',
          width: '200px',
          height: '200px',
          border: '2px solid rgba(255,255,255,0.2)',
          borderRadius: '50%',
          animation: 'float 6s ease-in-out infinite'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '15%',
          left: '5%',
          width: '150px',
          height: '150px',
          border: '2px solid rgba(255,255,255,0.2)',
          transform: 'rotate(45deg)',
          animation: 'float 8s ease-in-out infinite'
        }} />

        <div className="container mx-auto px-4" style={{position: 'relative', zIndex: 1}}>
          <div className="max-w-5xl mx-auto text-center">
            {/* Logo */}
            <div className="mb-12 flex justify-center">
              <img 
                src="/logos/osagest-logo-white.png" 
                alt="OSAGEST" 
                style={{
                  height: 'auto',
                  width: '280px',
                  maxWidth: '90%',
                  filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.2))'
                }}
              />
            </div>
            
            {/* Main Headline */}
            <h1 style={{
              fontSize: '48px',
              fontWeight: 600,
              color: '#FFFFFF',
              marginBottom: '24px',
              lineHeight: 1.2,
              letterSpacing: '-0.02em'
            }}>
              Plataforma de Gestión Integral
            </h1>
            
            {/* Subheadline */}
            <p style={{
              fontSize: '24px',
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.9)',
              marginBottom: '16px',
              lineHeight: 1.4
            }}>
              Para Autónomos y Pymes Españolas
            </p>
            
            <p style={{
              fontSize: '16px',
              color: 'rgba(255, 255, 255, 0.85)',
              marginBottom: '48px',
              lineHeight: 1.6,
              maxWidth: '700px',
              margin: '0 auto 48px'
            }}>
              Facturación, contabilidad, CRM, ticketing y digitalización en una sola plataforma
            </p>
            
            {/* CTAs */}
            <div style={{display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap'}}>
              <button 
                onClick={() => navigate('/registro')}
                style={{
                  fontSize: '15px',
                  fontWeight: 600,
                  backgroundColor: '#FF8C00',
                  color: '#FFFFFF',
                  padding: '16px 40px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(255, 140, 0, 0.3)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(255, 140, 0, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(255, 140, 0, 0.3)';
                }}
              >
                Empieza tu prueba gratuita
                <ArrowRight size={20} />
              </button>
              
              <button 
                onClick={() => navigate('/login')}
                style={{
                  fontSize: '15px',
                  fontWeight: 600,
                  backgroundColor: 'transparent',
                  color: '#FFFFFF',
                  padding: '16px 40px',
                  borderRadius: '8px',
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
                  e.target.style.borderColor = '#FFFFFF';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                }}
              >
                Iniciar Sesión
              </button>
            </div>
            
            {/* Trial info */}
            <p style={{
              marginTop: '32px',
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.8)',
              fontWeight: 400
            }}>
              ✨ 14 días de prueba gratuita • Sin tarjeta de crédito
            </p>
          </div>
        </div>
      </div>

      {/* Features Cards - Right after hero */}
      <div style={{
        backgroundColor: '#FFFFFF',
        padding: '60px 0',
        marginTop: '-50px',
        position: 'relative',
        zIndex: 2
      }}>
        <div className="container mx-auto px-4">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {features.map((feature, index) => (
              <div 
                key={index}
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  padding: '32px',
                  boxShadow: '0 2px 8px rgba(42, 42, 42, 0.08)',
                  border: '1px solid #E8E8E8',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 102, 179, 0.15)';
                  e.currentTarget.style.borderColor = '#0066B3';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(42, 42, 42, 0.08)';
                  e.currentTarget.style.borderColor = '#E8E8E8';
                }}
              >
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '12px',
                  backgroundColor: '#E8F4FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px',
                  color: '#0066B3'
                }}>
                  {feature.icon}
                </div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: '#2A2A2A',
                  marginBottom: '12px'
                }}>
                  {feature.title}
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#6B6B6B',
                  lineHeight: 1.6
                }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Módulos Section */}
      <div style={{
        backgroundColor: '#F8F9FA',
        padding: '80px 0'
      }}>
        <div className="container mx-auto px-4">
          <div className="text-center" style={{marginBottom: '64px'}}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 600,
              color: '#2A2A2A',
              marginBottom: '16px'
            }}>
              Módulos Disponibles
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#6B6B6B',
              fontWeight: 400
            }}>
              Todo lo que necesitas para gestionar tu negocio
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '24px',
            maxWidth: '1400px',
            margin: '0 auto'
          }}>
            {modules.map((module, index) => (
              <div 
                key={index}
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  padding: '32px 24px',
                  boxShadow: '0 2px 8px rgba(42, 42, 42, 0.08)',
                  border: '1px solid #E8E8E8',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 16px rgba(42, 42, 42, 0.12)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(42, 42, 42, 0.08)';
                }}
              >
                <div style={{
                  color: '#0066B3',
                  marginBottom: '16px',
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  {module.icon}
                </div>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#2A2A2A',
                  marginBottom: '12px'
                }}>
                  {module.title}
                </h3>
                <p style={{
                  fontSize: '13px',
                  color: '#6B6B6B',
                  lineHeight: 1.5
                }}>
                  {module.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div style={{
        backgroundColor: '#FFFFFF',
        padding: '80px 0'
      }}>
        <div className="container mx-auto px-4">
          <div className="text-center" style={{marginBottom: '64px'}}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 600,
              color: '#2A2A2A',
              marginBottom: '16px'
            }}>
              Planes y Precios
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#6B6B6B',
              fontWeight: 400
            }}>
              Elige el plan que mejor se adapte a tus necesidades
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {plans.map((plan, index) => (
              <div 
                key={index}
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  padding: '40px',
                  border: plan.popular ? '2px solid #0066B3' : '1px solid #E8E8E8',
                  boxShadow: plan.popular ? '0 8px 24px rgba(0, 102, 179, 0.15)' : '0 2px 8px rgba(42, 42, 42, 0.08)',
                  position: 'relative',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = plan.popular 
                    ? '0 12px 32px rgba(0, 102, 179, 0.2)' 
                    : '0 8px 16px rgba(42, 42, 42, 0.12)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = plan.popular 
                    ? '0 8px 24px rgba(0, 102, 179, 0.15)' 
                    : '0 2px 8px rgba(42, 42, 42, 0.08)';
                }}
              >
                {plan.popular && (
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#0066B3',
                    color: '#FFFFFF',
                    padding: '6px 16px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <Star size={14} />
                    MÁS POPULAR
                  </div>
                )}
                
                <h3 style={{
                  fontSize: '24px',
                  fontWeight: 600,
                  color: '#2A2A2A',
                  marginBottom: '16px'
                }}>
                  {plan.name}
                </h3>
                
                <div style={{marginBottom: '32px'}}>
                  <span style={{
                    fontSize: '48px',
                    fontWeight: 700,
                    color: '#0066B3'
                  }}>
                    {plan.price}€
                  </span>
                  <span style={{
                    fontSize: '16px',
                    color: '#6B6B6B',
                    fontWeight: 400
                  }}>
                    /mes
                  </span>
                </div>
                
                <ul style={{marginBottom: '32px'}}>
                  {plan.features.map((feature, fIndex) => (
                    <li 
                      key={fIndex}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        marginBottom: '12px',
                        fontSize: '14px',
                        color: '#2A2A2A'
                      }}
                    >
                      <CheckCircle2 
                        size={20}
                        style={{
                          color: '#00A86B',
                          marginRight: '12px',
                          flexShrink: 0,
                          marginTop: '2px'
                        }}
                      />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button
                  onClick={() => navigate('/registro')}
                  style={{
                    width: '100%',
                    fontSize: '15px',
                    fontWeight: 600,
                    backgroundColor: plan.popular ? '#0066B3' : 'transparent',
                    color: plan.popular ? '#FFFFFF' : '#0066B3',
                    padding: '14px 24px',
                    borderRadius: '8px',
                    border: plan.popular ? 'none' : '2px solid #0066B3',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: plan.popular ? '0 4px 12px rgba(0, 102, 179, 0.15)' : 'none'
                  }}
                  onMouseOver={(e) => {
                    if (plan.popular) {
                      e.target.style.backgroundColor = '#004D85';
                    } else {
                      e.target.style.backgroundColor = '#E8F4FF';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (plan.popular) {
                      e.target.style.backgroundColor = '#0066B3';
                    } else {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  Comenzar Ahora
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        backgroundColor: '#2A2A2A',
        padding: '48px 0',
        color: '#FFFFFF'
      }}>
        <div className="container mx-auto px-4">
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '24px'
          }}>
            <img 
              src="/logos/osagest-logo-white.png" 
              alt="OSAGEST" 
              style={{
                height: 'auto',
                width: '200px'
              }}
            />
            <p style={{
              fontSize: '13px',
              color: 'rgba(255, 255, 255, 0.7)',
              fontWeight: 400,
              textAlign: 'center'
            }}>
              © 2025 OSAGEST - Plataforma de Gestión para Autónomos y Pymes. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
      `}</style>
    </div>
  );
}

export default LandingOSAGEST;
